import { useCallback, useEffect, useRef } from 'react';
import { useMessageStore } from '../store';

export const useMessageDraft = (channelId: string) => {
	const { getEditorDraft, setEditorDraft } = useMessageStore();
	const saveDraftTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Create a debounced version of the draft save function
	const debounceSaveDraft = useCallback(
		(content: string) => {
			// Clear any existing timeout
			if (saveDraftTimeoutRef.current) {
				clearTimeout(saveDraftTimeoutRef.current);
			}

			// Set a new timeout to save the draft after 500ms of inactivity
			saveDraftTimeoutRef.current = setTimeout(() => {
				setEditorDraft(channelId, content);
			}, 500);
		},
		[channelId, setEditorDraft]
	);

	// Get saved draft for current channel
	const getSavedDraft = useCallback(() => {
		return getEditorDraft(channelId);
	}, [channelId, getEditorDraft]);

	// Clear draft immediately
	const clearDraft = useCallback(() => {
		// Clear any pending timeout
		if (saveDraftTimeoutRef.current) {
			clearTimeout(saveDraftTimeoutRef.current);
		}
		// Clear the draft from the store
		setEditorDraft(channelId, '');
	}, [channelId, setEditorDraft]);

	// Clear timeout on unmount
	useEffect(() => {
		return () => {
			if (saveDraftTimeoutRef.current) {
				clearTimeout(saveDraftTimeoutRef.current);
			}
		};
	}, []);

	return {
		debounceSaveDraft,
		getSavedDraft,
		clearDraft,
	};
};
